import { useRef, useState } from "react";

import {
  faCheck,
  faEye,
  faSparkles,
  faUndo,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Pill } from "src/base-components/Pill";
import { OriginalValueWindow } from "src/cases/caseView/components/OriginalValueWindow";
import type { CaseTemplateFormFieldsInner } from "src/clients/case-management";
import { Toolbar } from "src/design-system/Toolbar";
import { Tooltip } from "src/design-system/Tooltip";

type HintedFieldRowProps = {
  fieldKey: string;
  field: CaseTemplateFormFieldsInner;
  isHinted: boolean;
  originalValue: unknown;
  onApprove: () => void;
  onReject: () => void;
  onRewriteWithCopilot: () => void;
  disabled: boolean;
  children: React.ReactNode;
};

export const HintedFieldRow: React.FC<HintedFieldRowProps> = ({
  fieldKey,
  field,
  isHinted,
  originalValue,
  onApprove,
  onReject,
  onRewriteWithCopilot,
  disabled,
  children,
}) => {
  const [isOriginalValueOpen, setIsOriginalValueOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasOriginalValue = Array.isArray(originalValue)
    ? originalValue.length > 0
    : originalValue != null && originalValue !== "";

  return (
    <div
      ref={containerRef}
      className="group/hint relative"
      data-loc="hinted-field-row"
    >
      {children}
      <div className="absolute -top-2 right-0 z-10 flex items-center gap-x-1.5 opacity-0 transition-opacity group-hover/hint:opacity-100">
        {isHinted && hasOriginalValue && (
          <Tooltip
            placement="top"
            title="Show original value"
            delay={200}
            asChild
          >
            <Pill
              dataLoc={`copilot-edited-pill-${fieldKey}`}
              size="sm"
              variant="indigo-light"
              onClick={() => setIsOriginalValueOpen((prev) => !prev)}
            >
              <Pill.Icon icon={faSparkles} />
              <Pill.Text>Edited by Copilot</Pill.Text>
              <Pill.Icon icon={faEye} />
            </Pill>
          </Tooltip>
        )}
        <Toolbar dataLoc={`hint-toolbar-${fieldKey}`}>
          {isHinted && (
            <>
              <Toolbar.IconButton
                dataLoc="toolbar-accept-button"
                disabled={disabled}
                icon={faCheck}
                tooltip="Accept"
                onClick={onApprove}
              />
              <Toolbar.IconButton
                dataLoc="toolbar-dismiss-button"
                disabled={disabled}
                icon={hasOriginalValue ? faUndo : faXmark}
                tooltip={hasOriginalValue ? "Revert to original" : "Dismiss"}
                onClick={onReject}
              />
            </>
          )}
          <Toolbar.IconButton
            dataLoc="toolbar-rewrite-with-copilot-button"
            disabled={disabled}
            icon={faSparkles}
            tooltip="Rewrite with Copilot"
            onClick={onRewriteWithCopilot}
          />
        </Toolbar>
      </div>
      {isHinted && hasOriginalValue && (
        <OriginalValueWindow
          anchorRef={containerRef}
          field={field}
          isOpen={isOriginalValueOpen}
          originalValue={originalValue}
          onClose={() => setIsOriginalValueOpen(false)}
        />
      )}
    </div>
  );
};
